<script setup lang="ts">
/**
 * The chat scope browser (§6b) — an expandable panel above the composer rather than a dropdown.
 *
 * Why columns and not a tree: in a tree a client row has to be BOTH selectable ("scope to Palieri")
 * and expandable ("show Palieri's cases"), and filtering it on search either hides parents whose
 * children matched or keeps rows that don't match. Miller columns make navigation and selection the
 * same act — clicking a client scopes to it AND fills the next column — which is also how /app/docs
 * already reads. Searching swaps the columns for a flat result list instead of filtering them.
 *
 * There is no Apply button: the deepest highlighted row IS the scope, so the chip below updates as
 * you browse. Selecting therefore does not close the panel (that would make exploring hostile);
 * Escape, the chip, and sending a message close it.
 *
 * This is the browsing BODY only. It has no card chrome of its own: `ChatScopeBar` renders it as an
 * expansion of the composer card, so the border and rounding are the card's. The height animation
 * lives there too.
 */
import { Building2, Users, ChevronRight, ChevronLeft, Loader2, MessageSquare, Check } from 'lucide-vue-next'
import type { Case } from '~/stores/cases'
import { useScopePanel } from '~/composables/web-chat/useScopePanel'
import { scopeLabel, type ChatScope } from '~~/shared/web-chat/scope'
import {
  buildScopeGroups, searchScopeLadder, NO_CLIENT_KEY,
  type ScopeClientGroup, type ScopeHit,
} from '~~/shared/web-chat/scopeLadder'
import SearchInput from '~/components/controls/SearchInput.vue'

defineProps<{
  /** Docked sidebar / Word add-in: too narrow for columns, falls back to single-column drill-in. */
  compact?: boolean
}>()

const { t } = useI18n()
const webChat = useWebChatStore()
// The lists are shared with ChatScopeBar (fetched once per session), not refetched here.
const { close, clients, cases, loaded, loading, ensureScopeLists } = useScopePanel()

const query = ref('')
const searchRef = ref<InstanceType<typeof SearchInput> | null>(null)

/** Column-1 selection. Not the same as the scope: browsing "Sin cliente" navigates without scoping. */
const browsingClient = ref<string | null>(null)

/**
 * Column 1. The rules live in `shared/web-chat/scopeLadder.ts` so they can be tested without
 * mounting this component — chiefly that a client with NO CASES still gets a row
 * (AlexAmin/marcus#85), which is the whole point of the client list being fetched rather than
 * derived from the cases.
 */
const groups = computed<ScopeClientGroup<Case>[]>(
  () => buildScopeGroups(clients.value, cases.value, t('clients.hub.scopeNoClient')),
)

const activeGroup = computed(() => groups.value.find(g => g.key === browsingClient.value) ?? null)

const searching = computed(() => query.value.trim().length > 0)

/** Flat search list, both rungs. See `searchScopeLadder` for why clients match and sort first. */
const results = computed<ScopeHit<Case>[]>(() => searchScopeLadder(groups.value, cases.value, query.value))

const scope = computed<ChatScope>(() => webChat.activeScope)

/**
 * Row states, shared by all four lists (search, compact, and both columns) so the same state never
 * looks like two different things depending on which list you're in.
 *
 * Selection is an INTERACTIVE state, so it carries the interactive hue — a full-strength `accent`
 * left bar over a faint `accent-surface` wash. It used to be a slab of full Limestone
 * (`bg-accent-light`), which is a warm-neutral SURFACE color: it read as heavy beige furniture
 * rather than "this row is the scope", and at row size it dominated the panel.
 *
 * Every row reserves the 2px left border (transparent when idle) so text never shifts as the
 * marker appears.
 */
const ROW = 'flex w-full items-center gap-2 rounded-md border-l-2 py-1.5 pl-1.5 pr-2 text-left text-sm transition-colors'
const ROW_SELECTED = 'border-accent-ink bg-accent-surface font-medium text-foreground'
const ROW_IDLE = 'border-transparent hover:bg-muted'
/** Browsing a client without having scoped to it — Archive Sand rule, no fill (a second fill made
 *  'browsing' and 'hovered' indistinguishable, since `bg-muted` is the hover surface). */
const ROW_BROWSING = 'border-secondary hover:bg-muted'

function isScoped(kind: ChatScope['kind'], id?: string | null) {
  if (kind === 'global') return scope.value.kind === 'global'
  return scope.value.kind === kind && scope.value.id === id
}

/**
 * Clear the scope. Surfaced as a footer ACTION, never as a row in the list: "all documents" is the
 * absence of a scope, not a peer of the clients, and as a list item it rendered as the highlighted
 * selection among them — which read as if global were one of the clients.
 */
function pickGlobal() {
  browsingClient.value = null
  void webChat.setScope({ kind: 'global' })
}

/**
 * Narrowing never prompts any more: an attached document stays readable from any rung (MAR-114), so
 * switching case or client can no longer orphan one. The confirm step that used to guard this — and
 * the `/api/items/in-scope` check behind it — asked the user to authorize a loss that no longer
 * happens.
 */
function applyScope(next: ChatScope) {
  void webChat.setScope(next)
}

/** Navigation IS selection — except for the unassigned bucket, which has no client to scope to. */
function pickClient(group: ScopeClientGroup<Case>) {
  browsingClient.value = group.key
  if (group.id) applyScope({ kind: 'client', id: group.id, name: group.name })
}

function pickCase(m: Case) {
  browsingClient.value = m.client_id ?? NO_CLIENT_KEY
  applyScope({
    kind: 'case',
    id: m.id,
    name: m.name,
    clientId: m.client_id,
    // `?? null`: `client_name` is absent (not null) on a case row returned by `PUT /api/cases/:id`,
    // which the store caches by merge. See `shared/cases/case.ts`.
    clientName: m.client_name ?? null,
  })
}

/**
 * Going back up the ladder DESELECTS the rung you're leaving: stepping out of a client's case list
 * widens the scope to that client rather than silently keeping the case selected while showing the
 * client list. Navigation and selection are the same act here, so they have to agree in both
 * directions.
 */
function leaveGroup() {
  const group = activeGroup.value
  browsingClient.value = null
  if (!group) return
  if (scope.value.kind !== 'case') return
  void webChat.setScope(group.id
    ? { kind: 'client', id: group.id, name: group.name }
    : { kind: 'global' })
}

const scoped = computed(() => scope.value.kind !== 'global')

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}

// The parent mounts this component only while the panel is open, so open-time setup belongs in
// onMounted — no watcher on the shared flag.
onMounted(() => {
  void ensureScopeLists()
  // Start the browser where the chat already is, so opening shows the current scope in context
  // instead of resetting to the top of the ladder.
  const s = scope.value
  if (s.kind === 'case') browsingClient.value = s.clientId ?? NO_CLIENT_KEY
  else if (s.kind === 'client') browsingClient.value = s.id
  nextTick(() => searchRef.value?.focus())
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <!-- Fills whatever height the parent gives it (50vh docked, full screen on mobile). The height is
       FIXED rather than content-driven: letting it follow the columns made the whole composer jump
       every time you moved between a client and its cases. -->
  <div class="flex h-full min-h-0 flex-col">
    <!-- No close button up here: it would sit directly beneath the bar's own X (which CLEARS the
         scope) — two identical glyphs, stacked, doing different things. Closing is the footer's
         primary action instead. -->
    <div class="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
      <span class="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {{ t('clients.hub.scopePanelTitle') }}
      </span>
      <SearchInput
        ref="searchRef"
        v-model="query"
        :placeholder="t('clients.hub.scopeSearch')"
        variant="bare"
        size="sm"
        class="min-w-0 flex-1"
      />
    </div>

    <!-- `!loaded`, not `loading && !loaded`. The old guard only held while a fetch was actually in
         flight, so the frames before one started — and any frame where a concurrent caller already
         owned the `loading` flag — fell through to the columns and rendered an empty 50vh slab. The
         panel appeared to open onto nothing for several seconds. "Not trustworthy yet" is the
         condition that matters, and `loaded` is the flag that means it. -->
    <div v-if="!loaded" class="flex flex-1 items-center justify-center gap-2 px-3 py-6 text-sm text-muted-foreground">
      <Loader2 class="h-4 w-4 animate-spin" /> {{ t('common.loading') }}
    </div>

    <!-- ── Search results: a flat list REPLACING the columns ─────────────────── -->
    <div v-else-if="searching" class="min-h-0 flex-1 overflow-y-auto p-1">
      <p v-if="!results.length" class="px-2 py-4 text-center text-sm text-muted-foreground">
        {{ t('clients.hub.scopeNoResults') }}
      </p>
      <!-- Both rungs in one list, each keeping the icon and the trailing detail it carries in the
           columns (case count for a client, client name for a case), so a row means the same thing
           here as it does there. -->
      <button
        v-for="hit in results"
        :key="hit.key"
        type="button"
        :class="[ROW, (hit.kind === 'client' ? isScoped('client', hit.group.id) : isScoped('case', hit.item.id))
          ? ROW_SELECTED
          : ROW_IDLE]"
        @click="hit.kind === 'client' ? pickClient(hit.group) : pickCase(hit.item)"
      >
        <template v-if="hit.kind === 'client'">
          <Users class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ hit.group.name }}</span>
          <span class="shrink-0 text-xs text-muted-foreground">{{ hit.group.cases.length }}</span>
        </template>
        <template v-else>
          <Building2 class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ hit.item.name }}</span>
          <span v-if="hit.item.client_name" class="shrink-0 truncate text-xs text-muted-foreground">{{ hit.item.client_name }}</span>
        </template>
      </button>
    </div>

    <!-- ── Compact / mobile: single column with drill-in ──────────────────────── -->
    <div v-else-if="compact" class="min-h-0 flex-1 overflow-y-auto p-1 [&_button]:py-2.5">
      <template v-if="!activeGroup">
        <!-- Same dual meaning as the desktop column, so the same affordance: a check for the half
             the chevron hides, and a title that names both effects. -->
        <button
          v-for="g in groups"
          :key="g.key"
          type="button"
          :class="[ROW, isScoped('client', g.id) ? ROW_SELECTED : ROW_IDLE]"
          :title="g.id ? t('clients.hub.scopeWholeClientHint', { client: g.name }) : t('clients.hub.scopeNoClientHint')"
          @click="pickClient(g)"
        >
          <Users class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ g.name }}</span>
          <span class="shrink-0 text-xs text-muted-foreground">{{ g.cases.length }}</span>
          <Check v-if="isScoped('client', g.id)" class="h-3.5 w-3.5 shrink-0 text-accent-ink" />
          <ChevronRight class="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
    </template>
      <template v-else>
        <!-- Backing out of a client's cases WIDENS the scope from the case to the whole client
             (`leaveGroup`) — the deepest highlighted row is the scope, and stepping up un-highlights
             the case. That is the documented model, but "back" universally reads as undoing
             navigation, not as changing what the assistant can see, so it has to say so here. -->
        <button
          type="button"
          class="text-muted-foreground"
          :class="[ROW, ROW_IDLE]"
          :title="scope.kind === 'case' && activeGroup.id
            ? t('clients.hub.scopeWidenHint', { client: activeGroup.name })
            : t('clients.hub.scopeBack')"
          @click="leaveGroup"
        >
          <ChevronLeft class="h-3.5 w-3.5 shrink-0" />
          <span class="truncate">{{ activeGroup.name }}</span>
        </button>
        <div class="my-1 h-px bg-border" />
        <button
          v-for="m in activeGroup.cases"
          :key="m.id"
          type="button"
          :class="[ROW, isScoped('case', m.id) ? ROW_SELECTED : ROW_IDLE]"
          @click="pickCase(m)"
        >
          <Building2 class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ m.name }}</span>
        </button>
    </template>
    </div>

    <!-- ── Full: miller columns ──────────────────────────────────────────────── -->
    <div v-else class="grid min-h-0 flex-1 grid-cols-2 divide-x divide-border">
      <!-- Column 1: clients -->
      <div class="min-h-0 overflow-y-auto p-1">
        <p class="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {{ t('clients.hub.scopeClients') }}
        </p>

        <!-- No groups at all means the org has neither clients nor unassigned cases — the old copy
             here said "no cases", which stopped being the reason this column can be empty. -->
        <p v-if="!groups.length" class="px-2 py-3 text-center text-sm text-muted-foreground">
          {{ t('clients.hub.scopeEmpty') }}
        </p>
        <button
          v-for="g in groups"
          :key="g.key"
          type="button"
          :class="[
            ROW,
            isScoped('client', g.id)
              ? ROW_SELECTED
              : browsingClient === g.key ? ROW_BROWSING : ROW_IDLE,
          ]"
          :title="g.id ? t('clients.hub.scopeWholeClientHint', { client: g.name }) : t('clients.hub.scopeNoClientHint')"
          @click="pickClient(g)"
        >
          <Users class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ g.name }}</span>
          <span class="shrink-0 text-xs text-muted-foreground">{{ g.cases.length }}</span>
          <!-- This row does two things at once — it opens the client's cases in column 2 AND scopes
               to the whole client (feature doc §6: the deepest highlighted row IS the scope, there is
               no Apply). A lone chevron announced only the first of those, so users drilled in to
               look around and silently changed what Marcus could see. The check states the half the
               chevron was hiding; the title says both in words. -->
          <Check v-if="isScoped('client', g.id)" class="h-3.5 w-3.5 shrink-0 text-accent-ink" />
          <ChevronRight class="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </div>

      <!-- Column 2: the browsed client's cases -->
      <div class="min-h-0 overflow-y-auto p-1">
        <p class="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {{ t('clients.hub.scopeCases') }}
        </p>
        <p v-if="!activeGroup" class="px-2 py-3 text-center text-sm text-muted-foreground">
          {{ t('clients.hub.scopePickClient') }}
        </p>
        <button
          v-for="m in activeGroup?.cases || []"
          :key="m.id"
          type="button"
          :class="[ROW, isScoped('case', m.id) ? ROW_SELECTED : ROW_IDLE]"
          @click="pickCase(m)"
        >
          <Building2 class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ m.name }}</span>
        </button>
      </div>
    </div>

    <!-- Once a scope is picked the user usually wants to go back and type. The collapse chevron on the
         bar is a control, not an invitation — this is the primary way out. -->
    <!-- Actions only — the current scope is already stated on the bar directly above this panel, and
         repeating it here just said the same long label twice on one screen. -->
    <div class="flex shrink-0 items-center justify-end gap-2 border-t border-border px-3 py-2">
      <button
        v-if="scoped"
        type="button"
        class="shrink-0 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        @click="pickGlobal"
      >
        {{ t('clients.hub.scopeClear') }}
      </button>
      <Button type="button" size="sm" class="shrink-0 gap-1.5" @click="close">
        <MessageSquare class="h-3.5 w-3.5" />
        {{ t('clients.hub.scopeBackToChat') }}
      </Button>
    </div>
  </div>
</template>
